import { LoadingButton } from '@mui/lab';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useTranslate } from 'src/locales';
import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';
import { IContactListItem } from 'src/services/contacts/contacts.types';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import { openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { useAppDispatch } from 'src/store/store';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';
import { useCommunicationActions, buildDialNumber } from 'src/sections/admin/integrations/communications/use-communication-actions';

import ContactsNotesModal from 'src/components/custom-tables/contacts-table/contacts-notes-modal';

// ----------------------------------------------------------------------

interface ContactRowActionsProps {
  row: IContactListItem;
  /** Opens the context-preserving record panel (JT1-1529) instead of hard-navigating to the profile. */
  onView: (contactId: string) => void;
  onSendEmail: (row: IContactListItem) => void;
  onRemove: (row: IContactListItem) => void;
  isRemoving?: boolean;
}

/**
 * Contact row kebab for the shared DataList (JT1-1528). Ports the old contacts-table-row actions:
 * View, Notes (local modal, needs the thread id), Send Email (feature-gated), Call/SMS (when comms
 * is active — the SMS modal must render at this persistent root), Add Task, and Remove (confirm).
 * Reads the flat IContactListItem the lean endpoint returns.
 */
export default function ContactRowActions({ row, onView, onSendEmail, onRemove, isRemoving }: ContactRowActionsProps) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const popover = usePopover();
  const tenantType = useOrgTenant();
  const { featureEnabled } = useFeatureFlags();

  const notes = useBoolean();
  const confirm = useBoolean();

  const commsName = `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim();
  const comms = useCommunicationActions(buildDialNumber(row.phone ?? undefined), commsName, { entity_type: CommunicationEntityType.CONTACT, entity_id: row.id });

  const item = (icon: string, label: string, onClick: () => void, color?: string) => (
    <MenuItem sx={color ? { color } : undefined} onClick={() => { popover.onClose(); onClick(); }}>
      <Iconify icon={icon} />
      <Typography variant="body2">{label}</Typography>
    </MenuItem>
  );

  return (
    <>
      <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
        <Iconify icon="eva:more-horizontal-fill" />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="right-top" sx={{ width: 200 }}>
        {item('mdi:eye', t('common.view'), () => onView(row.id))}

        {row.thread_id && (
          <MenuItem onClick={() => { popover.onClose(); notes.onTrue(); }}>
            <Iconify icon="mdi:comments" />
            <Typography variant="body2">{t('common.notes')}</Typography>
          </MenuItem>
        )}

        {(featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED) && tenantType !== TenantType.Candidate && row.email) &&
          item('lsicon:email-send-filled', t('common.send_email'), () => onSendEmail(row))}

        {comms.commsActive && (
          <>
            {item('solar:phone-bold', t('integrations.communications.actions.call'), () => comms.call())}
            {item('solar:chat-round-dots-bold', t('integrations.communications.actions.sms'), () => comms.openSms())}
          </>
        )}

        {item('ic:round-add-task', t('common.add_task'), () => dispatch(openTaskEditorSidesheet({ contactId: row.id })))}

        {item('eva:trash-2-outline', t('common.remove'), () => confirm.onTrue(), 'error.main')}
      </CustomPopover>

      {row.thread_id && (
        <ContactsNotesModal threadId={row.thread_id} open={notes.value} onClose={notes.onFalse} />
      )}

      {comms.smsModal}

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('contacts.remove.title', { defaultValue: 'Remove contact' })}
        content={t('contacts.remove.confirm', { defaultValue: 'Are you sure you want to remove this contact?' })}
        action={
          <LoadingButton
            loading={isRemoving}
            variant="contained"
            color="error"
            onClick={() => { onRemove(row); confirm.onFalse(); }}
          >
            {t('common.remove')}
          </LoadingButton>
        }
      />
    </>
  );
}
